import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { AlertModule } from 'ngx-bootstrap/alert';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Topic } from '../../content/topics/topic.model';
import { TopicService } from '../../content/topics/topics.service';
import { Culture } from '../../cultures/culture.model';
import { CultureService } from '../../cultures/culture.service';
import { NormalisedHttpError } from '../../error';
import { reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent, LoadingObject, LoadingService } from '../../loaders';
import {
  NEWS_DIGEST_CADENCES,
  NewsDigestCadence,
  NewsServiceSubscription,
  Subscription,
  SubscriptionCriterion,
  SubscriptionService,
  SubscriptionSwitches,
} from './subscription.service';

/** What a subscription is to, as the screen names its kind. */
type CriterionKind = 'application' | 'culture' | 'show';

const KIND_OF: Record<SubscriptionCriterion['type'], CriterionKind> = { topics: 'application', cultures: 'culture', shows: 'show' };

/** Each kind's name, as a root Transloco key. */
const KIND_LABELS: Record<CriterionKind, string> = { application: 'common.application', culture: 'common.culture', show: 'common.show_noun' };

/**
 * The reader's subscriptions, in two kinds:
 *
 * - **The news service**: a digest of Interculturalist news by email, on the
 *   reader's cadence, with every item or the big ones only. Never in the app.
 * - **Subscriptions** to an application, a national culture or a show: each new
 *   item about it, at once, in the app, by email, or both, by two switches.
 *   Switching the last one off unsubscribes. Shows are subscribed to from the
 *   show's own page.
 *
 * Names come from the topic, culture and show lists, because a subscription
 * identifies its criterion by slug alone.
 */
@Component({
  selector: 'multisite-subscriptions',
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.scss'],
  imports: [FormsModule, RouterModule, TranslocoModule, AlertModule, LoaderGlobeAnimationDetailComponent],
})
export class SubscriptionsComponent {
  private subscriptionService = inject(SubscriptionService);
  private topicService = inject(TopicService);
  private cultureService = inject(CultureService);
  private loadingService = inject(LoadingService);

  readonly cadences = NEWS_DIGEST_CADENCES;
  readonly kindLabels = KIND_LABELS;

  subscriptions = signal<Subscription[]>([]);
  applications = signal<Topic[]>([]);
  cultures = signal<Culture[]>([]);
  showNames = signal<Map<string, string>>(new Map());
  error = signal<string | null>(null);

  /** The reader's news service, or null when they have none; undefined until loaded. */
  newsService = signal<NewsServiceSubscription | null | undefined>(undefined);
  /** What the reader will take the news service with: their own when they have it, the defaults otherwise. */
  newsChoice = signal<NewsServiceSubscription>({ cadence: 'weekly', bigNewsOnly: true });

  newApplication = '';
  newCulture = '';

  loadingObject = signal<LoadingObject>({});
  isAnythingLoading = computed(() => this.loadingService.isAnythingLoading(this.loadingObject()));

  /** The reader's subscriptions, with a name and a kind to show for each. */
  rows = computed(() =>
    this.subscriptions().map((subscription) => ({
      subscription,
      name: this.nameOf(subscription.criterion),
      kind: subscription.criterion ? KIND_OF[subscription.criterion.type] : 'application',
    })),
  );

  /** What the reader can still subscribe to: each criterion may be held once. */
  availableApplications = computed(() => this.applications().filter((topic) => !this.isSubscribed({ type: 'topics', id: topic.slug })));
  availableCultures = computed(() => this.cultures().filter((culture) => !this.isSubscribed({ type: 'cultures', id: culture.slug })));

  constructor() {
    this.load();
    reloadOnLocaleChange(() => this.loadNames());
  }

  setLoadingStatus(key: string, value: boolean): void {
    this.loadingObject.update((current) => this.loadingService.setLoadingStatus(key, value, current));
  }

  /** Choose the cadence or the coverage: saved at once for a reader who has the news service. */
  chooseNews(change: Partial<NewsServiceSubscription>): void {
    const choice = { ...this.newsChoice(), ...change };
    this.newsChoice.set(choice);

    if (this.newsService()) {
      this.saveNewsService(choice);
    }
  }

  chooseCadence(cadence: NewsDigestCadence): void {
    this.chooseNews({ cadence });
  }

  takeNewsService(): void {
    this.saveNewsService(this.newsChoice());
  }

  leaveNewsService(): void {
    this.run('news-service', this.subscriptionService.leaveNewsService(), () => this.newsService.set(null));
  }

  subscribe(criterion: SubscriptionCriterion): void {
    this.run('subscriptions:create', this.subscriptionService.subscribe(criterion), (created) => {
      this.subscriptions.update((current) => [...current, created]);
      this.newApplication = '';
      this.newCulture = '';
    });
  }

  /** Turn one switch on or off; turning off the last one unsubscribes. */
  toggle(subscription: Subscription, which: keyof SubscriptionSwitches, on: boolean): void {
    const switches = { inApp: subscription.inApp, email: subscription.email, [which]: on };

    if (!switches.inApp && !switches.email) {
      this.unsubscribe(subscription);
      return;
    }

    this.run('subscriptions:' + subscription.id, this.subscriptionService.changeSwitches(subscription.id, { [which]: on }), (updated) =>
      this.subscriptions.update((current) => current.map((s) => (s.id === updated.id ? updated : s))),
    );
  }

  unsubscribe(subscription: Subscription): void {
    this.run('subscriptions:' + subscription.id, this.subscriptionService.unsubscribe(subscription.id), () =>
      this.subscriptions.update((current) => current.filter((s) => s.id !== subscription.id)),
    );
  }

  private saveNewsService(choice: NewsServiceSubscription): void {
    this.run('news-service', this.subscriptionService.putNewsService(choice), (saved) => {
      this.newsService.set(saved);
      this.newsChoice.set(saved);
    });
  }

  private load(): void {
    this.run('subscriptions:list', this.subscriptionService.getSubscriptions(), (subscriptions) => {
      this.subscriptions.set(subscriptions);
      this.loadShowNames();
    });
    this.run('news-service', this.subscriptionService.getNewsService(), (newsService) => {
      this.newsService.set(newsService);
      if (newsService) {
        this.newsChoice.set(newsService);
      }
    });
    this.loadNames();
  }

  private loadNames(): void {
    this.run('criteria:applications', this.topicService.requestTopics('interculturalist', 'platform'), (topics) =>
      this.applications.set(topics.filter((topic) => topic.category === 'interculturalist' && topic.type === 'platform')),
    );
    this.run('criteria:cultures', this.cultureService.getCultures('geographic', 'national', false), (cultures: Culture[]) =>
      this.cultures.set([...cultures].sort((a, b) => a.name.localeCompare(b.name))),
    );
    this.loadShowNames();
  }

  /** Shows' names, asked for only when the reader subscribes to a show. */
  private loadShowNames(): void {
    if (this.subscriptions().some((subscription) => subscription.criterion?.type === 'shows')) {
      this.run('criteria:shows', this.subscriptionService.getShowNames(), (names) => this.showNames.set(names));
    }
  }

  private run<T>(key: string, request: Observable<T>, next: (value: T) => void): void {
    this.error.set(null);
    this.setLoadingStatus(key, true);
    request.pipe(finalize(() => this.setLoadingStatus(key, false))).subscribe({
      next,
      error: (error: NormalisedHttpError | string) => this.error.set(typeof error === 'string' ? error : error.message),
    });
  }

  private isSubscribed(criterion: SubscriptionCriterion): boolean {
    return this.subscriptions().some((s) => s.criterion?.type === criterion.type && s.criterion?.id === criterion.id);
  }

  private nameOf(criterion: SubscriptionCriterion | null): string {
    if (!criterion) {
      return '';
    }

    switch (criterion.type) {
      case 'topics':
        return this.applications().find((topic) => topic.slug === criterion.id)?.name ?? criterion.id;
      case 'cultures':
        return this.cultures().find((culture) => culture.slug === criterion.id)?.name ?? criterion.id;
      case 'shows':
        return this.showNames().get(criterion.id) ?? criterion.id;
    }
  }
}
